import { converter, formatHex, parse, toGamut, wcagContrast } from 'culori'
import type { ThemeContrast, ThemeTokens } from '../theme/presets'

const rgb = converter('rgb')
const oklch = converter('oklch')
const gamut = toGamut('rgb')
const hex = (color: Parameters<typeof formatHex>[0]) => formatHex(gamut(color)!)!

function blend(foreground: string, background: string, alpha: number): string {
  const a = rgb(foreground)!
  const b = rgb(background)!
  return formatHex({ mode: 'rgb', r: a.r! * alpha + b.r! * (1 - alpha),
    g: a.g! * alpha + b.g! * (1 - alpha), b: a.b! * alpha + b.b! * (1 - alpha) })!
}

function tokenColor(tokens: ThemeTokens, key: string, fallback: string, background: string): string {
  let value = tokens[key] || fallback
  // Presets use aliases, and imported themes may use translucent colors.
  const seen = new Set<string>([key])
  while (value.startsWith('var(')) {
    const alias = /^var\((--[\w-]+)\)$/.exec(value)?.[1]
    if (!alias || seen.has(alias)) return fallback
    seen.add(alias)
    value = tokens[alias] || fallback
  }
  const color = parse(value) || parse(fallback)!
  return blend(formatHex(color)!, background, color.alpha ?? 1)
}

/** Derive opaque status pairs after all theme inputs have been resolved.
 * Keep semantic hues and limit foreground lightness changes; if those cannot
 * pass, move the pill background instead. Check quantized colors in both states.
 */
export function toolbarStatusTokens(tokens: ThemeTokens, mode: ThemeContrast): ThemeTokens {
  const target = (mode === 'high' ? 7 : 4.5) + 0.08
  const surface = tokenColor(tokens, '--surface-bg', '#123f3a', '#ffffff')
  const toolbar = tokenColor(tokens, '--toolbar-bg', '#1f7a72', surface)
  const white = `rgb(${tokens['--white-rgb'] || '255, 255, 255'})`
  const initialBg = blend(white, toolbar, 0.05)
  const initialHover = blend(white, toolbar, 0.08)
  const result: ThemeTokens = {}

  for (const [name, fallback] of Object.entries({ error: '#f87171', success: '#34d399', warning: '#fbbf24', neutral: '#c5baa0' })) {
    const seed = tokenColor(tokens, name === 'neutral' ? '--text-muted' : `--${name}`, fallback, initialBg)
    const source = oklch(seed)!
    const candidates = [seed]
    for (let step = 1; step <= 24; step += 1) {
      for (const direction of [-1, 1]) {
        const l = Math.max(0, Math.min(1, source.l! + direction * step * 0.005))
        const candidate = hex({ ...source, l })
        // Avoid reaching the target by washing a semantic color into white.
        if ((oklch(candidate)!.c ?? 0) >= (source.c ?? 0) * 0.7) candidates.push(candidate)
      }
    }
    const foregroundOn = (bg: string, hover: string) => candidates.find(color =>
      wcagContrast(color, bg) >= target && wcagContrast(color, hover) >= target)
    let bg = initialBg
    let hover = initialHover
    let foreground = foregroundOn(bg, hover)
    if (!foreground) {
      const base = oklch(initialBg)!
      // Nearest background lightness first; preserve its hue as well.
      for (let step = 1; step <= 200 && !foreground; step += 1) {
        for (const direction of [-1, 1]) {
          const attempt = hex({ ...base, l: Math.max(0, Math.min(1, base.l! + direction * step / 200)) })
          const attemptHover = blend(seed, attempt, 0.03)
          const match = foregroundOn(attempt, attemptHover)
          if (match) { bg = attempt; hover = attemptHover; foreground = match; break }
        }
      }
    }
    if (!foreground) {
      // Extreme imported colors can leave no solution within the preferred
      // lightness range. Retain the hue and use a guaranteed endpoint pair.
      const light = source.l! >= 0.5
      foreground = hex({ ...source, l: light ? 0.92 : 0.2, c: Math.min(source.c ?? 0, 0.08) })
      bg = light ? '#000000' : '#ffffff'
      hover = blend(foreground, bg, 0.03)
    }
    result[`--toolbar-status-${name}`] = foreground
    result[`--toolbar-status-${name}-bg`] = bg
    result[`--toolbar-status-${name}-hover-bg`] = hover
  }
  return result
}

/** The color Type Guide toolbars mark active controls with, drawn on the page
 * itself. It is the theme's secondary color where that already reads on the
 * page (3:1); otherwise it is moved in lightness, toward the page's opposite,
 * until it reads comfortably (4.5:1) rather than barely.
 */
export function pageMarkColor(secondary: string, page: string, readable = 3, target = 4.5): string {
  const seed = parse(secondary)
  const paper = parse(page)
  if (!seed || !paper) return secondary
  const start = hex(seed)
  if (wcagContrast(start, page) >= readable) return start
  const minimum = target
  const source = oklch(seed)!
  const direction = (oklch(paper)!.l ?? 1) > 0.5 ? -1 : 1
  for (let step = 1; step <= 100; step += 1) {
    const candidate = hex({ ...source, l: Math.max(0, Math.min(1, source.l! + direction * step * 0.01)) })
    if (wcagContrast(candidate, page) >= minimum) return candidate
  }
  return direction < 0 ? '#000000' : '#ffffff'
}

export function toolbarMarkToken(tokens: ThemeTokens, page: string): ThemeTokens {
  return { '--toolbar-mark': pageMarkColor(tokenColor(tokens, '--secondary', '#1f7a72', page), page) }
}
